import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function BitcoinTechPage88() {
  const contentRef = useRef(null);
  const titleText = "Compact Block Relay and Bandwidth optimization";
  const links = {
    prev: "/bitcoin-tech-page-87",
    index: "/index-bitcoin-tech",
    next: "/bitcoin-tech-page-89",
  };
  const questions = [
    {
      text: "1. What primary inefficiency in Bitcoin’s original block relay protocol did BIP-152 aim to fix?",
      options: [
        {
          value: "A",
          label: "A. Excessive proof-of-work verification by full nodes",
        },
        {
          value: "B",
          label:
            "B. Redundant transmission of full transaction data that nodes already had in their mempools",
        },
        {
          value: "C",
          label: "C. The inability of miners to broadcast blocks securely",
        },
        {
          value: "D",
          label: "D. The lack of encryption in peer-to-peer connections",
        },
      ],
      correct: "B",
    },
    {
      text: "2. What information is primarily sent in a compact block instead of full transactions?",
      options: [
        {
          value: "A",
          label: "A. Full scripts and signatures for every transaction",
        },
        {
          value: "B",
          label: "B. Only the block hash and previous block reference",
        },
        {
          value: "C",
          label:
            "C. Short transaction IDs along with a few prefilled full transactions",
        },
        {
          value: "D",
          label: "D. Merkle proofs for every transaction output",
        },
      ],
      correct: "C",
    },
    {
      text: "3. What determines whether a receiving node must request additional data after receiving a compact block?",
      options: [
        {
          value: "A",
          label:
            "A. Whether the block’s proof of work meets the difficulty target",
        },
        {
          value: "B",
          label:
            "B. Whether the node’s mempool contains the transactions referenced by the short IDs",
        },
        {
          value: "C",
          label: "C. Whether the sender is operating in high-bandwidth mode",
        },
        {
          value: "D",
          label: "D. Whether the block size exceeds the maximum limit",
        },
      ],
      correct: "B",
    },
    {
      text: "4. What is the main trade-off between low-bandwidth mode and high-bandwidth mode in compact block relay?",
      options: [
        {
          value: "A",
          label:
            "A. Low-bandwidth mode improves security, while high-bandwidth mode weakens validation",
        },
        {
          value: "B",
          label:
            "B. Low-bandwidth mode favors miners, while high-bandwidth mode favors full nodes",
        },
        {
          value: "C",
          label:
            "C. Low-bandwidth mode minimizes data usage but adds latency, while high-bandwidth mode uses slightly more bandwidth to achieve faster propagation",
        },
        {
          value: "D",
          label:
            "D. Low-bandwidth mode disables transaction requests, while high-bandwidth mode forces full block downloads",
        },
      ],
      correct: "C",
    },
  ];

  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={88}
        timerMinutes={60}
        currentLevel={6}
        unlockedBadges={11}
      />
      <p className="content" ref={contentRef}>
        Welcome to <b>Day 88</b>! Today’s lesson explores Compact Block Relay as
        defined in BIP-152, a key improvement to Bitcoin’s peer-to-peer
        networking layer. It explains why traditional full-block propagation was
        inefficient, how compact blocks reduce redundant data transmission, and
        the mechanics that allow nodes to reconstruct blocks using their
        mempools. By examining message flow, operational modes, version
        differences, and real-world trade-offs, learners gain a clear
        understanding of how BIP-152 improves bandwidth efficiency, block
        propagation speed, and overall network health while preserving Bitcoin’s
        decentralized design.
        <br />
        <br />
        <b>Background and Motivation</b>
        <br />
        <br />
        Before the introduction of BIP-152, Bitcoin’s peer-to-peer protocol
        relied on a very straightforward block propagation mechanism. When a
        miner found a block, it would broadcast the full block, including the
        header and every transaction, to its peers. Each of those peers would
        then validate the block and forward it further through the network. The
        critical observation was that almost all of the transactions included in
        a new block were already known to most nodes since they were previously
        received as unconfirmed transactions in their mempools.
        <br />
        This setup led to significant redundancy. A block several megabytes in
        size would often contain thousands of transactions, most of which the
        node already had stored. Relaying this redundant information again
        consumed both bandwidth and time, often causing bandwidth spikes and
        slower propagation, especially for nodes on ordinary consumer internet
        connections.
        <br />
        BIP-152, finalized in April 2016, directly addressed this inefficiency.
        Its stated purpose was to introduce compact blocks on the wire to save
        bandwidth for nodes across the Bitcoin network. The motivation section
        of BIP-152 makes this clear, noting that the Bitcoin P2P protocol had
        historically been inefficient for block relay. It also pointed out that
        the problem of bandwidth spikes and delayed block propagation could be
        mitigated by avoiding sending redundant data. By minimizing the amount
        of information transferred, particularly redundant transaction data, the
        Bitcoin network could become more bandwidth-efficient and propagate
        blocks faster and more smoothly.
        <br />
        <br />
        <b>Protocol Specification and Mechanics</b>
        <br />
        <br />
        The compact block relay protocol works by transmitting only the minimum
        necessary data to allow peers to reconstruct a full block using their
        local mempool.
        <br />
        In compact block relay, instead of sending complete transactions, the
        sending node sends short transaction identifiers, or short IDs, for
        transactions it assumes the peer already has. If the sender suspects
        that the peer might not have certain transactions, it sends those
        transactions in full.
        <br />
        In version 1 of BIP-152, these short IDs are based on the transaction ID
        (txid), while version 2 uses witness transaction IDs (wtxids) that
        support SegWit transactions. Each short ID is 6 bytes long and is a
        non-cryptographic hash that can quickly and efficiently reference a
        transaction without the need to send the full 32-byte identifier.
        <br />
        The receiving peer uses its mempool to recreate the full block. It
        calculates the same short ID mapping using a shared nonce and block
        header information. It then tries to match the short IDs in the compact
        block message to transactions it already has. If all matches are found,
        the block can be reconstructed immediately without further data
        exchange. If not, the node requests only the missing transactions from
        the sender.
        <br />
        <br />
        <b>Message Flow</b>
        <br />
        <br />
        Two nodes must first negotiate whether they support compact blocks and
        which mode to use. This negotiation happens through <i>
          sendcmpct
        </i>{" "}
        message
        <br />
        When a node receives a new block in high-bandwidth mode, it immediately
        sends a <i>cmpctblock</i> message to its peer. This message includes the
        block header, a nonce for short-ID generation, a list of short IDs for
        all transactions the sender expects the peer to know, and a small list
        of complete transactions that the peer likely does not have, such as the
        coinbase transaction.
        <br />
        The receiving peer processes this message and attempts to rebuild the
        block. If all transactions are available, it completes the block
        instantly. If any are missing, the peer sends a <i>getblocktxn</i>{" "}
        request specifying the missing indices, and the sender responds with a{" "}
        <i>blocktxn</i> message containing the required transactions.
        <br />
        Once the receiver has all transactions, it validates and adds the block
        to its local chain.
        <br />
        In low-bandwidth mode, the sender does not automatically send the
        compact block. It first announces the block header hash or uses an inv
        message. The receiver can then decide to request the compact block if
        necessary. This avoids redundant data transmission to peers that may
        have already received the block from another connection.
        <br />
        <br />
        <b>Modes of Operation</b>
        <br />
        <br />
        Compact block relay supports two operational modes, allowing nodes to
        trade off between bandwidth use and propagation speed.
        <ul>
          <li>
            {" "}
            Low-bandwidth mode is designed for general use with most peers. The
            node announces only the header of a new block, and the peer requests
            the compact block only if it does not already have it. This mode
            conserves bandwidth by avoiding unnecessary data exchange.
          </li>
          <li>
            {" "}
            High-bandwidth mode is designed for a few trusted peers. In this
            mode, a node immediately sends the compact block when it receives a
            new block. Although this may result in sending data to peers that
            already have the block, compact blocks are so small that even
            redundant transmissions consume less bandwidth than the traditional
            full block relay. The benefit is much faster propagation across the
            network.
          </li>
        </ul>
        <br />
      </p>
      <table className="trade-table">
        <tr>
          <th>Feature</th>
          <th>Low Bandwidth Mode</th>
          <th>High Bandwidth Mode</th>
        </tr>

        <tr>
          <td>Purpose</td>
          <td>Designed for most peers to minimize unnecessary data transfer</td>
          <td>
            Designed for a few trusted peers to accelerate block propagation
          </td>
        </tr>
        <tr>
          <td>When Compact Block Is Sent</td>
          <td>
            Only sent if the peer explicitly requests it after receiving the
            block header
          </td>
          <td>
            Automatically sent as soon as the node receives and validates the
            block header’s Proof of Work
          </td>
        </tr>
        <tr>
          <td>Bandwidth Usage</td>
          <td>Very low, since data is only sent on request</td>
          <td>
            Slightly higher, because blocks are proactively sent to peers even
            if they already have them
          </td>
        </tr>
        <tr>
          <td>Latency</td>
          <td>
            Slightly higher, as an extra round-trip (request and response) is
            needed
          </td>
          <td>Lower, as the block is sent immediately after discovery</td>
        </tr>
        <tr>
          <td>Use Case</td>
          <td>Typical connections between ordinary nodes</td>
          <td>
            Strategic connections between miners and fast-relay nodes to ensure
            rapid block propagation
          </td>
        </tr>
        <tr>
          <td>Data Redundancy</td>
          <td>Minimal - avoids sending duplicate block data</td>
          <td>May include redundant data if peers already have the block</td>
        </tr>
        <tr>
          <td>Number of Peers Recommended</td>
          <td>Can be used with most or all peers</td>
          <td>Recommended for only a few high-trust or high-speed peers</td>
        </tr>
        <tr>
          <td>Efficiency Goal</td>
          <td>Optimize bandwidth efficiency</td>
          <td>Optimize propagation speed</td>
        </tr>
        <tr>
          <td>Trade-off</td>
          <td>Higher efficiency, slower propagation</td>
          <td>Lower efficiency, faster propagation</td>
        </tr>
      </table>
      <p className="content">
        <b>Version Differences</b>
        <br />
        <br />
        Version 1 uses short IDs based on txids, while Version 2 uses wtxids and
        supports SegWit transactions. This upgrade allows the protocol to remain
        compatible with modern transaction formats while maintaining bandwidth
        efficiency.
        <br />
        <br />
        <b>Bandwidth Efficiency</b>
        <br />
        <br />
        Before compact blocks, every node received the entire block, even though
        it already had most of the transactions in its mempool. This meant that
        each block relay duplicated transaction data across the network. Each
        node was effectively downloading data it already possessed, wasting both
        time and bandwidth.
        <br />
        Compact blocks eliminate most of this redundancy. Instead of sending all
        transaction data, the sending node transmits only short IDs for known
        transactions and sends full transactions only for those that the peer
        likely lacks.
        <br />
        As a result, a block that would normally be several megabytes can often
        be reduced to just a few kilobytes. This reduction directly saves
        bandwidth and indirectly improves propagation time, as smaller data
        packets travel faster over TCP connections.
        <br />
        <br />
        <b>Latency and Propagation Effects</b>
        <br />
        <br />
        Compact blocks not only save bandwidth but also enhance propagation
        speed. Because smaller data packets experience less network congestion
        and buffer delay, blocks reach more nodes faster. This reduces the time
        it takes for most nodes to become aware of a newly mined block, which in
        turn reduces the likelihood of competing blocks and stale blocks.
        <br />
        Although latency reduction was not the original goal of BIP-152, it is
        an important benefit that improves the overall health of the Bitcoin
        network. Faster block propagation decreases the risk of orphan blocks
        and contributes to fairer conditions for miners.
        <br />
        Empirical data has shown that while bandwidth savings are dramatic, the
        latency improvements are moderate, generally within the range of 0 to 20
        percent depending on network conditions and mempool overlap.
        <br />
        <br />
        <b>Mempool Synchronization and Practical Considerations</b>
        <br />
        <br />
        The effectiveness of compact block relay depends on how closely the
        mempools of two peers match. If both peers have similar sets of
        unconfirmed transactions, the compact block can be reconstructed almost
        instantly without additional requests. If their mempools differ
        significantly, the receiver must request many missing transactions,
        introducing round trips and additional latency.
        <br />
        Nodes that maintain consistent relay policies, stay online for longer
        durations, and participate actively in transaction propagation generally
        enjoy higher mempool overlap and thus greater bandwidth savings.
        <br />
        <br />
        <b>Reconstruction Process</b>
        <br />
        <br />
        When a node receives a compact block, it first processes the prefilled
        transactions list, which always includes the coinbase transaction. It
        then attempts to match short IDs to transactions in its mempool. If all
        transactions are found, the node immediately reconstructs and validates
        the block. If not, it sends a <i>getblocktxn</i> message to request the
        missing transactions, waits for the <i>blocktxn</i> response, and
        completes the reconstruction.
        <br />
        Each missing transaction adds a round trip delay, so compact block relay
        works best when most transactions are already known.
        <br />
        <br />
        <b>Peer Behavior and Implementation Notes</b>
        <br />
        <br />
        Nodes use the <i>sendcmpct</i> message to negotiate whether compact
        blocks are supported and whether to use high or low bandwidth mode.
        Typically, only a few peers are configured for high-bandwidth mode to
        optimize propagation without wasting bandwidth.
        <br />
        The block header is always verified for proof of work before the block
        is relayed, ensuring that invalid headers are not propagated. The
        indices of prefilled transactions are efficiently encoded to further
        reduce message size.
        <br />
        The coinbase transaction is always included in full because peers cannot
        have it beforehand.
        <br />
        <br />
        <b>Measured Benefits</b>
        <br />
        <br />
        Bandwidth savings with compact blocks are substantial. In many cases, a
        block that would be several megabytes is reduced to just a few
        kilobytes. While this significantly decreases the bandwidth required for
        block relay, latency improvements are more modest because other factors
        such as transaction requests and validation still influence total
        propagation time.
        <br />
        Faster propagation contributes to network health by reducing orphan
        rates and ensuring more consistent block updates across the network. For
        node operators with limited bandwidth, the reduced block-relay load also
        improves stability and responsiveness.
        <br />
        <br />
        <b>Limitations and Trade-offs</b>
        <br />
        <br />
        Despite its advantages, compact block relay is not without limitations.
        If mempool overlap is low, many transactions will need to be requested,
        reducing the efficiency gains. Each additional round trip increases
        latency, offsetting the protocol’s benefits.
        <br />
        Another consideration is processing delay. Even after a block is
        reconstructed, the node must perform full validation before relaying it
        further. In some implementations, propagation benefits are reduced if
        block validation threads are blocked or delayed.
        <br />
        Security concerns also arise if a malicious node sends malformed compact
        blocks or intentionally withholds transactions to force additional
        bandwidth usage. To mitigate this, nodes must validate block headers
        before relaying and should use caution when accepting unverified compact
        blocks.
        <br />
        <br />
        <b>Extensions and Future Directions</b>
        <br />
        <br />
        Researchers and developers have explored several improvements to compact
        block relay. One approach involves prefilling more transactions beyond
        the coinbase, targeting those that the sender predicts the peer might
        not have. Studies show this can reduce the number of missing transaction
        requests by nearly 30 percent and reduce reconstruction latency by
        around 40 percent.
        <br />
        Other approaches include weak blocks and erasure coding. Weak blocks
        involve sending lower proof-of-work templates to speed up propagation,
        while erasure coding divides block data into coded fragments that can be
        efficiently reconstructed by peers. These approaches build upon the core
        concept introduced by compact block relay: transmitting the minimum data
        necessary for efficient block reconstruction.
        <br />
        <br />
        <b>The Myth of a Global Mempool</b>
        <br />
        <br />
        While it is true that similar mempool policies across the network
        enhance the efficiency of compact block relay, it is a misconception to
        believe that all nodes should maintain identical mempools. There is no
        such thing as a global Bitcoin mempool; each node independently manages
        its own set of unconfirmed transactions based on its local policies,
        bandwidth constraints, and trust preferences. Bitcoin’s design is
        inherently decentralized, allowing every participant to define their own
        validation and relay rules, such as minimum fee rates, transaction age
        limits, or replacement-by-fee (RBF) acceptance. This independence
        ensures resilience and censorship resistance. If all nodes followed the
        same policies, the network could become more uniform but also more
        vulnerable to coordinated manipulation or systemic inefficiencies. Thus,
        while compact block relay performs best when mempools are similar,
        Bitcoin’s true strength lies in its diversity of node policies that
        together maintain decentralization and robustness.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        Compact Block Relay, defined in BIP-152, was created to eliminate
        redundant data transmission during block propagation. Instead of sending
        the full block, nodes send a compact sketch of the block composed of the
        header, short transaction IDs, and only a few complete transactions.
        <br />
        This system relies on mempool similarity between peers to function
        efficiently. When mempools align, bandwidth use drops dramatically and
        blocks propagate faster across the network. Two operational modes,
        low-bandwidth and high-bandwidth, allow nodes to balance efficiency and
        speed depending on their needs.
        <br />
        While compact blocks greatly reduce network data usage, other
        constraints such as validation time, missing transaction requests, and
        network conditions still affect overall propagation performance.
        <br />
        Extensions like dynamic transaction prefilling, erasure coding, and weak
        block relay build upon the same concept to further improve scalability
        and efficiency. For node operators, keeping relay policies consistent
        and monitoring mempool overlap are key to realizing the full benefits of
        compact block relay.
        <br />
        Compact blocks represent an important evolution in Bitcoin’s networking
        layer, allowing faster and more efficient block relay while maintaining
        the robustness and decentralization that define the Bitcoin protocol.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)}
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
    </div>
  );
}

export default BitcoinTechPage88;
